import { createRef } from 'react';
import {
  typeSwitch,
  typePoeCamera,
  fieldProductSelection,
  fieldSecureBusinessOffers,
  tollFree,
  setup,
  keyServiceAddresses,
  keyEmergencyContact,
  keyMonitorIncludedEquipment,
  keyMonitorAdditionalEquipment,
  keySmartCameraIncludedEquipment,
  keyProtectIncludedEquipment,
  keyProtectIncludedEquipmentSelection,
  keyProtectAdditionalEquipment,
  keyControlIncludedEquipment,
  keyControlAdditionalEquipment,
  keySecurityCameraEquipment,
  keyControlIncludedEquipmentSelection,
  keyOfficePhoneData,
  keyTelusOptikTvAdditionalSetTopBoxes,
  fieldTelusOptikTvAdditionalSetTopBox,
  fieldSmartCameraOffers
} from '../utils/constant';
import {
  getFieldChecked,
  getFieldType,
  getFieldValue,
  getFieldName
} from './utils';

export const normalizeEquipmentGroupSection = equipmentGroupData => {
  const { data, isEditing, orderData, key, initFields } = equipmentGroupData;
  const result = initFields || {};

  if (data) {
    data.forEach(({ fields }) => {
      const {
        name: fieldKey,
        type,
        required,
        value,
        minValue,
        maxValue,
        minLength,
        maxLength,
        price,
        monthlyPayment,
        productType,
        oneTimePrice,
        threeYearEasyPayment,
        fiveYearEasyPayment,
        label
      } = fields;

      if (!Object.prototype.hasOwnProperty.call(result, key)) {
        const defaultValue =
          isEditing && orderData ? (orderData[key] || {})[fieldKey] : value;
        const ref = createRef();
        let isVisibleValue = true;
        //  Check if product type switch and hide if default or camera count zero
        if (productType === typeSwitch) {
          if (isEditing && orderData && orderData[key]) {
            const cameraTypeCount = data.reduce(
              (
                acc,
                { fields: { productType: optionProductType, name: productKey } }
              ) => {
                let sum = acc;
                if (
                  optionProductType === typePoeCamera &&
                  orderData[key][productKey]
                ) {
                  sum += parseInt(orderData[key][productKey] || 0, 10);
                }
                return sum;
              },
              0
            );
            isVisibleValue = cameraTypeCount > 0;
          } else {
            isVisibleValue = false;
          }
        }

        result[fieldKey] = {
          name: fieldKey,
          ref,
          type,
          value: defaultValue || value,
          initPrice: price || 0,
          price: price || 0,
          initMonthlyPayment: monthlyPayment || 0,
          monthlyPayment: monthlyPayment || 0,
          initOneTimePrice: oneTimePrice || 0,
          oneTimePrice: oneTimePrice || 0,
          initThreeYearEasyPayment: threeYearEasyPayment || 0,
          threeYearEasyPayment: threeYearEasyPayment || 0,
          initFiveYearEasyPayment: fiveYearEasyPayment || 0,
          initFiveYearMonthlyPayment : fiveYearEasyPayment || 0,
          fiveYearEasyPayment : fiveYearEasyPayment || 0,
          minValue,
          maxValue,
          minLength,
          maxLength,
          isRequired: required || false,
          isVisible: isVisibleValue,
          productType,
          fieldLabel: label
        };
      }
    });
  }

  return result;
};

export const isRelatedFieldExisted = (
  field,
  fields,
  orderData,
  isFormTouched
) => {
  const {
    appearsAfterField,
    appearsAfterValue,
    backendFieldValidation,
    checkAnyOneIsSelected
  } = field;
  if (!appearsAfterField && !backendFieldValidation) return true;
  let result = true;
  let i = 0;
  if (appearsAfterField) {
    while (i < appearsAfterField.length && result) {
      const relatedFieldName = getFieldName(appearsAfterField[i]);
      const relatedFieldType = getFieldType(relatedFieldName, fields);
      const relatedFieldValue =
        relatedFieldType === 'Checkbox'
          ? getFieldChecked(relatedFieldName, fields)
          : getFieldValue(relatedFieldName, fields);

      if (relatedFieldName) {
        if (
          fields[relatedFieldName] &&
          fields[relatedFieldName].isVisible &&
          relatedFieldValue
        ) {
          if (appearsAfterValue) {
            let isValueSelected;
            if (
              (relatedFieldName === fieldProductSelection ||
                relatedFieldName === fieldSecureBusinessOffers ||
                relatedFieldName === fieldSmartCameraOffers) &&
              relatedFieldValue &&
              relatedFieldValue.length
            ) {
              isValueSelected = appearsAfterValue.find(item =>
                relatedFieldValue.includes(item.fields.value)
              );
            } else {
              isValueSelected = appearsAfterValue.find(
                item => relatedFieldValue === item.fields.value
              );
            }
            result = result && typeof isValueSelected !== 'undefined';
            if (result && checkAnyOneIsSelected) {
              break;
            }
          }
        } else if (orderData && orderData[relatedFieldName]) {
          if (fields[relatedFieldName]) {
            if (
              (isFormTouched &&
                orderData[relatedFieldName] === relatedFieldValue) ||
              !isFormTouched
            ) {
              if (appearsAfterValue) {
                const isValueSelected = appearsAfterValue.find(
                  item => orderData[relatedFieldName] === item.fields.value
                );
                result = result && typeof isValueSelected !== 'undefined';
              }
            } else result = false;
          } else if (appearsAfterValue) {
            const isValueSelected = appearsAfterValue.find(
              item => orderData[relatedFieldName] === item.fields.value
            );
            result = result && typeof isValueSelected !== 'undefined';
          }
        } else {
          result = false;
        }
      }
      i += 1;
    }
  }

  if (backendFieldValidation) {
    let isVisible;
    for (let index = 0; index < backendFieldValidation.length; index += 1) {
      const {
        fields: { key, value }
      } = backendFieldValidation[index];

      const getValue = () => {
        let resultValue;
        if (Array.isArray(orderData[key])) {
          resultValue = !!orderData[key].filter(selectedValue =>
            value.split('|').includes(selectedValue)
          ).length;
        } else if (
          [setup, tollFree].includes(key) &&
          Array.isArray(orderData.officePhoneData)
        ) {
          resultValue = value
            .split('|')
            .includes(orderData.officePhoneData[0][key].value);
        } else {
          resultValue = value.split('|').includes(orderData[key]);
        }
        return resultValue;
      };

      const valueExistsInOrder = getValue();
      if (valueExistsInOrder && result) {
        isVisible = true;
        break;
      }
      isVisible =
        !!isVisible &&
        (valueExistsInOrder ||
          (value.split('|').includes('undefined') &&
            typeof orderData[key] === 'undefined'));
    }
    return isVisible;
  }

  return result;
};

const removeUnusedSelectionsIncludedEquipments = obj =>
  Object.entries(obj)
    .filter(([, value]) => !!value)
    .reduce((acc, item) => ({ ...acc, [item[0]]: item[1] }), {});

const getEquipmentGroupFormData = obj => {
  const result = Object.keys(obj)
    .filter(
      key =>
        (obj[key] || {}).isVisible &&
        typeof (obj[key] || {}).value !== 'undefined'
    )
    .reduce((acc, key) => {
      const { value } = obj[key];
      acc[key] = value;
      return acc;
    }, {});
  return result;
};

export const findField = (data, fieldName) => {
  let result = null;
  let i = 0;

  if (!data) return result;

  while (i < data.length && !result) {
    const item = data[i];
    const {
      sys: {
        contentType: {
          sys: { id }
        }
      },
      fields: itemFields
    } = item;
    if (id === 'formFieldsGroup') {
      result = findField(itemFields.fields, fieldName);
    } else if (id === 'formSection') {
      result = findField(itemFields.formFields, fieldName);
    } else if (itemFields.name === fieldName) {
      result = item;
    }
    i += 1;
  }

  return result;
};
const getAddressParts = value => {
  const addressParts = value && value.split(',');
  const partsLength = addressParts.length;
  if (partsLength > 3) {
    const cityValue = addressParts[partsLength - 3].trim();
    const provincePostalString = addressParts[partsLength - 2];
    if (
      provincePostalString &&
      provincePostalString.split(' ').length > 3
    ) {
      const [
        ,
        provinceValue = '',
        postalCodeValue = ''
      ] = provincePostalString.match(/(\D{2}) (\D\d\D \d\D\d)/);
      return {
        provinceValue,
        postalCodeValue,
        cityValue
      };
    }
  }
  return '';
};
export const getFormData = formFields => {
  const result = Object.keys(formFields).reduce((acc, key) => {
    const { type, value, checked } = formFields[key];
    acc[key] = type === 'Checkbox' ? checked : value || '';
    return acc;
  }, {});

  //  Customer's Service Addresses
  if (formFields[keyServiceAddresses]) {
    result[keyServiceAddresses] = formFields[keyServiceAddresses];
      }
      if (formFields.serviceAddress) {
        // result[keyServiceAddresses] = formFields[keyServiceAddresses];
        const { provinceValue, postalCodeValue, cityValue } =
              getAddressParts(formFields.serviceAddress.value) || {};
            const { serviceAddress } = formFields;
            result.keyStreetAddressService = serviceAddress.value;
            result.keyProvinceService = provinceValue;
            result.keyCityService = cityValue;
            result.keyPostalCodeService = postalCodeValue;
          }
  // Customer's Emgergency Contact Details
  if (formFields[keyEmergencyContact]) {
    result[keyEmergencyContact] = formFields[keyEmergencyContact];
  }
  if (formFields.files) {
    result.files = formFields.files;
  }
  if (formFields.tollFreePhone) {
    result.tollFree = formFields.tollFreePhone;
  }
  // Customer's Emgergency Contact Details
  if (formFields[keyEmergencyContact]) {
    result[keyEmergencyContact] = formFields[keyEmergencyContact];
  }

  // Secure Business Equipment Sections
  if (formFields[keyMonitorIncludedEquipment]) {
    result[keyMonitorIncludedEquipment] = getEquipmentGroupFormData(
      formFields[keyMonitorIncludedEquipment]
    );
    result[
      keyMonitorIncludedEquipment
    ] = removeUnusedSelectionsIncludedEquipments(
      result[keyMonitorIncludedEquipment]
    );
  }
  if (formFields[keyMonitorAdditionalEquipment]) {
    result[keyMonitorAdditionalEquipment] = getEquipmentGroupFormData(
      formFields[keyMonitorAdditionalEquipment]
    );
    result[
      keyMonitorAdditionalEquipment
    ] = removeUnusedSelectionsIncludedEquipments(
      result[keyMonitorAdditionalEquipment]
    );
  }
  if (formFields[keyProtectIncludedEquipment]) {
    result[keyProtectIncludedEquipment] = getEquipmentGroupFormData(
      formFields[keyProtectIncludedEquipment]
    );
    result[
      keyProtectIncludedEquipment
    ] = removeUnusedSelectionsIncludedEquipments(
      result[keyProtectIncludedEquipment]
    );
  }
  if (formFields[keyProtectIncludedEquipmentSelection]) {
    result[keyProtectIncludedEquipmentSelection] = getEquipmentGroupFormData(
      formFields[keyProtectIncludedEquipmentSelection]
    );
    result[
      keyProtectIncludedEquipmentSelection
    ] = removeUnusedSelectionsIncludedEquipments(
      result[keyProtectIncludedEquipmentSelection]
    );
  }
  if (formFields[keyProtectAdditionalEquipment]) {
    result[keyProtectAdditionalEquipment] = getEquipmentGroupFormData(
      formFields[keyProtectAdditionalEquipment]
    );
    result[
      keyProtectAdditionalEquipment
    ] = removeUnusedSelectionsIncludedEquipments(
      result[keyProtectAdditionalEquipment]
    );
  }
  if (formFields[keyControlIncludedEquipment]) {
    result[keyControlIncludedEquipment] = getEquipmentGroupFormData(
      formFields[keyControlIncludedEquipment]
    );
    result[
      keyControlIncludedEquipment
    ] = removeUnusedSelectionsIncludedEquipments(
      result[keyControlIncludedEquipment]
    );
  }
  if (formFields[keyControlAdditionalEquipment]) {
    result[keyControlAdditionalEquipment] = getEquipmentGroupFormData(
      formFields[keyControlAdditionalEquipment]
    );
    result[
      keyControlAdditionalEquipment
    ] = removeUnusedSelectionsIncludedEquipments(
      result[keyControlAdditionalEquipment]
    );
  }
  if (formFields[keyControlIncludedEquipmentSelection]) {
    result[keyControlIncludedEquipmentSelection] = getEquipmentGroupFormData(
      formFields[keyControlIncludedEquipmentSelection]
    );
    result[
      keyControlIncludedEquipmentSelection
    ] = removeUnusedSelectionsIncludedEquipments(
      result[keyControlIncludedEquipmentSelection]
    );
  }

  // office phone data
  if (formFields[keyOfficePhoneData]) {
    result[keyOfficePhoneData] = formFields[keyOfficePhoneData];
  }

  if (formFields[keySmartCameraIncludedEquipment]) {
    result[keySmartCameraIncludedEquipment] = getEquipmentGroupFormData(
      formFields[keySmartCameraIncludedEquipment]
    );
    result[
      keySmartCameraIncludedEquipment
    ] = removeUnusedSelectionsIncludedEquipments(
      result[keySmartCameraIncludedEquipment]
    );
  }
  if (formFields[keySecurityCameraEquipment]) {
    result[keySecurityCameraEquipment] = getEquipmentGroupFormData(
      formFields[keySecurityCameraEquipment]
    );
    result[
      keySecurityCameraEquipment
    ] = removeUnusedSelectionsIncludedEquipments(
      result[keySecurityCameraEquipment]
    );
  }

  // Additional Set Top Boxes for Optik TV
  if (formFields[keyTelusOptikTvAdditionalSetTopBoxes]) {
    const innerFields = Object.values(
      formFields[keyTelusOptikTvAdditionalSetTopBoxes]
    ).map(item => {
      const newItem = { ...item };
      if (newItem[fieldTelusOptikTvAdditionalSetTopBox] === undefined) {
        newItem[fieldTelusOptikTvAdditionalSetTopBox] = '';
      }
      return { ...newItem };
    });
    result[keyTelusOptikTvAdditionalSetTopBoxes] = innerFields;
  }

  return result;
};
